import { motion } from "framer-motion";
import { getFAIconClass } from "@juiceforward/narrative-schema/icon-utils";
import { Icon } from "./Icon";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";

interface SectionInterstitialProps {
  sectionLabel: string;
  icon?: string; // Font Awesome icon name (e.g., "fass chart-line")
}

export function SectionInterstitial({
  sectionLabel,
  icon,
}: SectionInterstitialProps) {
  // Resolve the icon class if one is specified (guards the decorative block).
  const faIcon = getFAIconClass(icon);

  return (
    <motion.div
      className="absolute inset-0 z-50 flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      {/* Backdrop with blur */}
      <motion.div
        className="absolute inset-0 bg-black/60 backdrop-blur-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      />

      {/* Content container */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-8">
        {/* Decorative line above */}
        <motion.div
          className="w-12 h-0.5 bg-white/30 rounded-full mb-8"
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5, ease: "easeOut" }}
        />

        {/* Icon with pulse animation */}
        {faIcon && (
          <motion.div
            className="text-5xl mb-6"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              delay: 0.1,
              duration: 0.5,
              type: "spring",
              stiffness: 200,
              damping: 15,
            }}
          >
            <motion.span
              animate={{ scale: [1, 1.1, 1] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="inline-block text-white"
            >
              <Icon icon={icon} />
            </motion.span>
          </motion.div>
        )}

        {/* Section title */}
        <motion.h1
          className="text-4xl md:text-5xl lg:text-6xl text-white tracking-tight"
          style={{
            fontFamily: "var(--font-narrative-header)",
            fontWeight:
              "var(--font-narrative-header-weight, 700)" as unknown as number,
          }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            delay: 0.3,
            duration: 0.6,
            ease: [0.16, 1, 0.3, 1],
          }}
        >
          {sectionLabel}
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-white/50 text-sm mt-4 tracking-widest uppercase"
          style={{
            fontFamily: "var(--font-narrative-body)",
            fontWeight:
              "var(--font-narrative-body-weight)" as unknown as number,
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
        >
          Up Next
        </motion.p>

        {/* Decorative line below */}
        <motion.div
          className="w-12 h-0.5 bg-white/30 rounded-full mt-8"
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5, ease: "easeOut" }}
        />

        {/* Loading indicator - Lottie heartbeat animation */}
        <motion.div
          className="mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.3 }}
        >
          <DotLottieReact
            autoplay
            loop
            src="/narratives/_shared/heartbeat.json"
            style={{ width: 60, height: 60 }}
          />
        </motion.div>
      </div>
    </motion.div>
  );
}
